import { MutableRefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Blog,
  City,
  Collection,
  Country,
  FilterOption,
  Image,
  OptionLocation,
  SimpleCollection,
  Tag,
  TravelItinerary,
} from 'src/types';
import classNames from 'classnames';
import experienceStyles from 'src/styles/modules/pages/experience.module.scss';
import FilterSvg from 'public/images/svg_icons/filter.svg';
import RouteSvg from 'public/images/svg_icons/route.svg';
import FileSvg from 'public/images/svg_icons/file.svg';
import MapSvg from 'public/images/svg_icons/map.svg';
import { FiChevronLeft, FiChevronRight, FiPlay, FiX } from 'react-icons/fi';
import ClientOnlyPortal from 'src/components/ClientOnlyPortal';
import useBreakpoints from 'src/hooks/useBreakpoints';
import cityStyles from 'src/styles/modules/pages/city.module.scss';
import { ApiRoutes, AppRoutes } from 'src/lib/routes';
import ImageWithFallback from './ImageWithFallback';
import Link from 'next/link';
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import { sortBy, uniqBy, max, min } from 'lodash-es';
import { ItemInner } from './filter/Item';
import { sortOptions } from 'src/lib/constants';
import ControlButtons from './filter/ControlButtons';
import useFilter from 'src/hooks/useFilter';
import useQuery from 'src/hooks/useQuery';
import { filterKeys } from 'src/contexts/FilterProvider';
import {
  timeOfDayOptions,
  childAgeOptions,
  durationOptions,
  tripTypeOptions,
} from 'src/lib/constants';
import { useRouter } from 'next/router';
import { Modal } from 'react-bootstrap';

const HIDE_COLLECTIONS_TAB_ROUTES = ['/collections/[id]', '/collections/[id]/[cateId]'];
const DEFAULT_NUMBER_ITEMS = 6;
const LIMIT_BLOG_ITEMS = 15;
type PopupType = 'filters' | 'blogs' | 'itineraries' | 'collections';

type Record = {
  id: number;
  slug: string;
  title?: string;
  name?: string;
  image?: Image;
  coverImgUrl?: string;
  kind?: string;
  location?: OptionLocation;
};
type TabItem = {
  tabName: string | undefined;
  kind: PopupType | 'collection' | 'collectionCategories';
  header?: string;
  sections: {
    sectionId: string;
    title: string;
    data: Record[];
  }[];
  isShow: boolean | undefined;
  link?: string | undefined;
};

const ItemCard: React.FC<{
  link: string;
  imageUrl: string | undefined;
  title: string;
  kind?: string;
}> = ({ link, title, imageUrl, kind }) => {
  return (
    <Link legacyBehavior prefetch={false} href={link}>
      <a className="tw-block tw-text-brand-navy">
        <div className="tw-mb-2 tw-outline-none tw-relative">
          <ImageWithFallback
            src={imageUrl}
            alt=""
            className="tw-static tw-object-cover tw-h-28 tw-w-full tw-border-2 tw-border-brand-green tw-border-solid tw-rounded-lg"
            fill
          />
          {kind === 'video' ? (
            <div className="md:tw-p-5 tw-p-4 tw-rounded-full tw-border-2 tw-border-white tw-absolute tw-transform tw-top-1/2 tw--translate-y-1/2 tw-left-1/2 tw--translate-x-1/2">
              <FiPlay
                strokeWidth={1}
                className="md:tw-h-6 md:tw-w-6 tw-h-5 tw-w-5 tw-text-white tw-absolute tw-transform tw-top-1/2 tw--translate-y-1/2 tw-left-1/2 tw--translate-x-1/2"
              />
            </div>
          ) : null}
        </div>
        <div className="tw-text-sm tw-text-center">{title}</div>
      </a>
    </Link>
  );
};

const Filters: React.FC<{}> = () => {
  const { filterRecord, filterParams } = useFilter();
  const { data } = useQuery<{
    activityTypes: Tag[];
    locations: OptionLocation[];
    cities: City[];
    suitables: Tag[];
    prices: [number, number];
  }>(
    filterRecord
      ? ApiRoutes.apiFilterItemsRoute(
          {},
          { record_id: filterRecord.id, record_type: filterRecord.type }
        ).toUrl()
      : null
  );
  const { activityTypes, locations, cities, suitables, prices } = data || {
    activityTypes: [],
    locations: [],
    cities: [],
    suitables: [],
    prices: [],
  };

  const formattedActivityTypes: FilterOption[] = useMemo(
    () =>
      uniqBy(
        activityTypes
          .filter((o) => !o.parentId)
          .concat(activityTypes.filter((o) => !!o.parent).map((o) => o.parent) as Tag[])
          .sort((a, b) => a.name.localeCompare(b.name)),
        'id'
      ).map((tag) => {
        return {
          label: tag.name,
          value: tag.id,
          children: activityTypes
            .filter((o) => o.parentId === tag.id)
            .map((o) => ({ label: o.name, value: o.id })),
        };
      }),
    [JSON.stringify(activityTypes)]
  );

  const formattedSuitables: FilterOption[] = useMemo(
    () => suitables.map((o) => ({ label: o.name, value: o.id })),
    [JSON.stringify(suitables)]
  );

  const formattedLocations: FilterOption[] = useMemo(
    () => locations.map((o) => ({ label: o.name, value: o.id })),
    [JSON.stringify(locations)]
  );

  const formattedCities: FilterOption[] = useMemo(
    () => cities.map((o) => ({ label: o.name, value: o.id })),
    [JSON.stringify(cities)]
  );

  const fieldNames = filterKeys.filter((k) => !['cityId', 'countryId'].includes(k));
  const split = (filterOptions: FilterOption[]): FilterOption[][] => {
    const splitIndex = Math.ceil(filterOptions.length / 2);
    const group1 = filterOptions.slice(0, splitIndex);
    const group2 = filterOptions.slice(splitIndex);
    return [group1, group2];
  };

  return (
    <>
      <div className="tw-pt-3">
        <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">Sort By</div>
        <div className="row">
          <div className="col-md-6">
            <ItemInner
              options={split(sortOptions)[0]}
              inputType={'radio'}
              currentValue={filterParams['sortBy']}
              fieldName={'sortBy'}
            />
          </div>
          <div className="col-md-6">
            <ItemInner
              options={split(sortOptions)[1]}
              inputType={'radio'}
              currentValue={filterParams['sortBy']}
              fieldName={'sortBy'}
            />
          </div>
        </div>
        <div className="tw-my-4 tw-border-t tw-border-grey-lighter"></div>
        <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">Activity Type</div>
        <div className="row">
          <div className="col-md-6">
            <ItemInner
              options={split(formattedActivityTypes)[0]}
              isNested
              inputType="checkbox"
              fieldName="activityType"
              prefixFieldName="all"
              currentValue={filterParams['activityType']}
            />
          </div>
          <div className="col-md-6">
            <ItemInner
              options={split(formattedActivityTypes)[1]}
              isNested
              inputType="checkbox"
              fieldName="activityType"
              prefixFieldName="all"
              currentValue={filterParams['activityType']}
            />
          </div>
        </div>
      </div>
      <div className="tw-my-4 tw-border-t tw-border-grey-lighter"></div>
      <div className="">
        <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">Time of Day</div>
        <div className="row">
          <div className="col-md-4 md:tw-pr-0">
            <ItemInner
              options={[timeOfDayOptions[0]]}
              inputType="checkbox"
              fieldName="time"
              prefixFieldName="all"
              currentValue={filterParams['time']}
            />
          </div>
          <div className="col-md-4 md:tw-px-0">
            <ItemInner
              options={[timeOfDayOptions[1]]}
              inputType="checkbox"
              fieldName="time"
              prefixFieldName="all"
              currentValue={filterParams['time']}
            />
          </div>
          <div className="col-md-4 md:tw-pl-0">
            <ItemInner
              options={[timeOfDayOptions[2]]}
              inputType="checkbox"
              fieldName="time"
              prefixFieldName="all"
              currentValue={filterParams['time']}
            />
          </div>
        </div>
      </div>
      {cities.length > 1 || locations.length > 0 ? (
        <>
          <div className="tw-my-4 tw-border-t tw-border-grey-lighter"></div>
          <div className="">
            <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">Location</div>
            {cities.length > 1 ? (
              <ItemInner
                options={formattedCities}
                inputType="checkbox"
                maxNumShownOption={4}
                fieldName="cityIds"
                prefixFieldName="all"
                currentValue={filterParams['cityIds']}
              />
            ) : locations.length > 0 ? (
              <ItemInner
                options={formattedLocations}
                inputType="checkbox"
                maxNumShownOption={4}
                fieldName="locationIds"
                prefixFieldName="all"
                currentValue={filterParams['locationIds']}
              />
            ) : null}
          </div>
        </>
      ) : null}
      {suitables.length > 0 ? (
        <>
          <div className="tw-my-4 tw-border-t tw-border-grey-lighter"></div>
          <div className="">
            <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">
              Suitable For
            </div>
            <ItemInner
              options={formattedSuitables}
              inputType="checkbox"
              fieldName="suitableFor"
              prefixFieldName="all"
              currentValue={filterParams['suitableFor']}
            />
          </div>
        </>
      ) : null}
      <div className="tw-my-4 tw-border-t tw-border-grey-lighter"></div>
      <div className="">
        <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">Child Age</div>
        <div className="row">
          <div className="col-md-4 md:tw-pr-0">
            <ItemInner
              options={[childAgeOptions[0]]}
              inputType="checkbox"
              fieldName="childAge"
              prefixFieldName="all"
              currentValue={filterParams['childAge']}
            />
          </div>
          <div className="col-md-4 md:tw-px-0">
            <ItemInner
              options={[childAgeOptions[1]]}
              inputType="checkbox"
              fieldName="childAge"
              prefixFieldName="all"
              currentValue={filterParams['childAge']}
            />
          </div>
          <div className="col-md-4 md:tw-pl-0">
            <ItemInner
              options={[childAgeOptions[2]]}
              inputType="checkbox"
              fieldName="childAge"
              prefixFieldName="all"
              currentValue={filterParams['childAge']}
            />
          </div>
        </div>
      </div>
      <div className="tw-my-4 tw-border-t tw-border-grey-lighter"></div>
      <div className="">
        <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">Duration</div>
        <div className="row">
          <div className="col-6">
            <ItemInner
              options={split(durationOptions)[0]}
              inputType="checkbox"
              fieldName="durations"
              prefixFieldName="all"
              currentValue={filterParams['durations']}
            />
          </div>
          <div className="col-6">
            <ItemInner
              options={split(durationOptions)[1]}
              inputType="checkbox"
              fieldName="durations"
              prefixFieldName="all"
              currentValue={filterParams['durations']}
            />
          </div>
        </div>
      </div>
      <div className="tw-my-4 tw-border-t tw-border-grey-lighter"></div>
      <div className="">
        <div className="tw-font-styleSans tw-font-semibold tw-text-lg tw-mb-4">Trip Type</div>
        <div className="row">
          <div className="col-md-4">
            <ItemInner
              options={[tripTypeOptions[0]]}
              inputType="radio"
              fieldName="tripType"
              prefixFieldName="all"
              currentValue={filterParams['tripType']}
            />
          </div>
          <div className="col-md-4">
            <ItemInner
              options={[tripTypeOptions[1]]}
              inputType="radio"
              fieldName="tripType"
              prefixFieldName="all"
              currentValue={filterParams['tripType']}
            />
          </div>
        </div>
      </div>

      <div className="md:tw-relative tw-sticky tw-bottom-0 tw-bg-brand-yellow tw-pb-6 md:tw-pb-0">
        <div className="tw-border-t tw-mt-8 tw-mb-6 tw-border-grey-lighter md:tw--mx-10 tw--mx-5"></div>
        <ControlButtons setShowPopover={() => {}} fieldNames={fieldNames} />
      </div>
    </>
  );
};

const ModalOrDiv: React.ReactFCWithChildren<{
  isModal: boolean;
  show: boolean;
  popupRef?: MutableRefObject<HTMLDivElement | null>;
  onClose: () => void;
}> = ({ isModal, show, popupRef, children, onClose }) => {
  return isModal ? (
    <Modal
      show={show}
      onHide={() => {
        onClose();
      }}
      dialogClassName="modal-dialog-bottom modal-almost-full-height--sp modal-dialog-scrollable modal-lg"
      contentClassName="tw-rounded-tl-2xl tw-rounded-tr-2xl"
    >
      <Modal.Body className="tw-py-0 tw-px-5">{children}</Modal.Body>
    </Modal>
  ) : (
    <div
      className={classNames(
        `tw-fixed tw-right-0 tw-w-screen md:tw-max-w-xl tw-z-1200 ${cityStyles.cityPopup}`,
        {
          'tw-hidden': !show,
        }
      )}
    >
      <div
        ref={popupRef}
        className="tw-bg-brand-yellow tw-px-10 tw-pb-6 tw-overflow-y-auto tw-overflow-x-hidden custom-scrollbar tw-h-full"
      >
        {children}
      </div>
    </div>
  );
};

const CountryCityPageNav: React.FC<{
  onClose?: () => void;
  outerPopupType?: PopupType;
  travelItineraries?: TravelItinerary[];
  listCollections?: Collection[];
  locationCollections?: Collection[];
  activityCollections?: Collection[];
  blogs?: Blog[];
  cities?: City[];
  collectionCategories?: SimpleCollection[];
  collection?: Collection;
  record: City | Country;
}> = ({
  onClose,
  outerPopupType,
  travelItineraries,
  activityCollections,
  listCollections,
  locationCollections,
  collectionCategories,
  collection,
  blogs,
  cities,
  record,
}) => {
  const router = useRouter();
  const [popupType, setPopupType] = useState<PopupType | undefined>(undefined);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const tabItemsRef = useRef<HTMLDivElement | null>(null);
  const { isScreenMd } = useBreakpoints();
  const [showMoreRecords, setShowMoreRecords] = useState<{ [k: string]: boolean }>({});
  const blogVideos = (blogs || []).filter((o) => o.kind === 'video');
  const blogOthers = (blogs || []).filter((o) => o.kind !== 'video');

  useEffect(() => {
    setPopupType(outerPopupType);
  }, [outerPopupType]);

  useEffect(() => {
    if (popupRef.current) {
      if (popupType) {
        disableBodyScroll(popupRef.current);
      } else {
        enableBodyScroll(popupRef.current);
      }
    }
  }, [popupType]);

  const tabItems: TabItem[] = [
    {
      tabName: 'Filters',
      kind: 'filters',
      isShow: true,
      sections: [{ sectionId: 'filters', title: 'Filters', data: [] }],
    },
    {
      tabName: 'Insider Tips',
      kind: 'blogs',
      isShow: blogs && blogs.length > 0,
      sections: [
        { sectionId: 'other_blogs', title: `${record.name} Insider Tips`, data: blogOthers.slice(0, LIMIT_BLOG_ITEMS) },
        { sectionId: 'video_blogs', title: `${record.name} Videos`, data: blogVideos.slice(0, LIMIT_BLOG_ITEMS) },
      ].filter((o) => o.data.length > 0),
    },
    {
      tabName: 'Itinerary Ideas',
      kind: 'itineraries',
      isShow: travelItineraries && travelItineraries.length > 0 ? true : false,
      sections: [
        {
          sectionId: 'itineraries',
          title: `${record.name} Itineraries`,
          data: travelItineraries || [],
        },
      ],
    },
    {
      tabName: 'Collections',
      kind: 'collections',
      header: 'Handpicked Collections',
      isShow: !HIDE_COLLECTIONS_TAB_ROUTES.includes(router.pathname) && [activityCollections, listCollections, locationCollections, cities].some((o) => (o || []).length > 0),
      sections: [
        { sectionId: 'list', title: 'Top Picks', data: listCollections || [] },
        { sectionId: 'activities', title: 'Activities', data: (activityCollections || []).map((o) => ({...o, name: o.tag ? o.tag.name : o.name}))},
        { sectionId: 'locations', title: 'Areas to Explore', data: (locationCollections || []).map((o) => ({...o, name: o.location ? o.location.name : o.name }))},
        { sectionId: 'cities', title: 'Areas to Explore', data: cities || [] },
      ].filter((o) => o.data.length > 0),
    },
    {
      tabName: collection?.name,
      kind: 'collection',
      isShow: collection ? true : false,
      sections: [],
      link: collection ? AppRoutes.collectionRoute({ id: collection.slugCode }).toUrl() : undefined,
    },
    ...(uniqBy(sortBy(collectionCategories, 'position') || [], 'id').map((cate) => ({
      tabName: cate.name,
      kind: 'collectionCategories',
      isShow: true,
      sections: [],
      link: cate.url,
    })) as TabItem[]),
  ];
  const activeTabItems = tabItems.filter((o) => o.isShow);
  const currentTab = popupType ? tabItems.find((o) => o.kind === popupType) : null;
  const indexStartQuickLink = activeTabItems.findIndex((o) =>
    ['collection', 'collectionCategories'].includes(o.kind)
  );

  const moveTab = (next?: boolean) => {
    const tabItemsEle = tabItemsRef.current;
    if (!tabItemsEle || !isScreenMd) return;
    const tabItemElements = Array.from(tabItemsEle.children) as HTMLElement[];
    const containerRect = tabItemsEle.getBoundingClientRect();
    const visibilityTabIndex = tabItemElements.map((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const isVisible = itemRect.left < containerRect.right && itemRect.right > containerRect.left;
      if (isVisible) {
        return index;
      }
    });

    if (next) {
      const scrollLeft = tabItemElements.slice(0, max(visibilityTabIndex)).reduce((memo, ele) => {
        return memo + (ele as HTMLElement).offsetWidth;
      }, 0);
      tabItemsEle.scroll({ left: scrollLeft, behavior: 'smooth' });
    } else {
      const itemVisibilityWidth = tabItemElements
        .slice(min(visibilityTabIndex), max(visibilityTabIndex))
        .reduce((memo, ele) => {
          return memo + (ele as HTMLElement).offsetWidth;
        }, 0);
      const scrollLeft = tabItemsEle.scrollLeft - itemVisibilityWidth;
      tabItemsEle.scroll({ left: scrollLeft < 0 ? 0 : scrollLeft, behavior: 'smooth' });
    }
  };

  const showArrowButton = () => {
    if (!tabItemsRef.current || !isScreenMd) return;
    const tabItemsEle = tabItemsRef.current;
    const prevTab = document.getElementById('prev-tab');
    const nextTab = document.getElementById('next-tab');
    if (prevTab instanceof HTMLElement) {
      if (tabItemsEle.scrollLeft <= 0) {
        prevTab.classList.remove('md:tw-block');
      } else {
        prevTab.classList.add('md:tw-block');
      }
    }
    if (nextTab instanceof HTMLElement && tabItemsEle.parentElement) {
      if (
        tabItemsEle.scrollWidth - tabItemsEle.scrollLeft <=
        tabItemsEle.parentElement.offsetWidth
      ) {
        nextTab.classList.remove('md:tw-block');
      } else {
        nextTab.classList.add('md:tw-block');
      }
    }
  };

  useEffect(() => {
    showArrowButton();
  }, [collectionCategories?.length]);

  useLayoutEffect(() => {
    showArrowButton();
    window.addEventListener('resize', showArrowButton);
    return () => {
      window.removeEventListener('resize', showArrowButton);
    };
  }, []);

  useEffect(() => {
    const tabItemsEle = tabItemsRef.current;
    if (!tabItemsEle || !isScreenMd) return;
    tabItemsEle.addEventListener('scroll', showArrowButton);
    return () => {
      tabItemsEle.removeEventListener('scroll', showArrowButton);
    };
  }, []);

  return activeTabItems.length > 0 ? (
    <>
      <div className={`tw-sticky tw-z-90 tw-bg-brand-yellow ${experienceStyles.pageNav}`}>
        <div className="container tw-font-styleSans tw-py-3 md:tw-py-4 tw-flex tw-justify-around tw-items-center">
          <div
            id="prev-tab"
            className="tw-h-7 tw-w-7 tw-hidden tw-bg-white tw-rounded-full tw-border-solid tw-border-grey-lighter tw-border tw-p-1.5 tw-mr-4"
          >
            <FiChevronLeft
              size={16}
              strokeWidth={2}
              className="tw-cursor-pointer tw-text-brand-navy tw-align-top"
              onClick={() => {
                moveTab();
              }}
            />
          </div>

          <div
            ref={tabItemsRef}
            className="tw-flex md:tw--mx-2 tw--ml-0.5 tw--mr-3.75 tw-text-sm tw-font-styleSans tw-font-semibold tw-overflow-x-auto tw-overflow-y-hidden tw-whitespace-nowrap hide-scrollbar tw-transition-transform"
          >
            {tabItems
              .filter((o) => o.isShow)
              .map((item, index) => (
                <div key={item.link} className="tw-flex tw-justify-center tw-items-center">
                  {indexStartQuickLink === index ? (
                    <div className="tw-border tw-border-l tw-h-6 md:tw-mx-2 tw-mx-1 tw-border-solid tw-border-grey-lighter"></div>
                  ) : null}
                  {item.link ? (
                    <div className="tw-bg-white tw-flex tw-justify-center tw-items-center tw-mx-0.5 md:tw-mx-2 md:tw-px-8 tw-px-4 tw-py-2 tw-rounded-4xl tw-border tw-cursor-pointer tw-border-grey-lighter hover:tw-border-brand-navy">
                      <Link legacyBehavior prefetch={false} href={item.link}>
                        <a className="tw-block tw-text-brand-navy">{item.tabName}</a>
                      </Link>
                    </div>
                  ) : (
                    <div
                      key={item.kind}
                      className="tw-bg-white tw-flex tw-justify-center tw-items-center tw-mx-0.5 md:tw-mx-2 md:tw-px-8 tw-px-4 tw-py-2 tw-rounded-4xl tw-border tw-cursor-pointer tw-border-grey-lighter hover:tw-border-brand-navy"
                      onClick={() => {
                        setPopupType(item.kind as PopupType);
                      }}
                    >
                      {item.kind === 'filters' ? (
                        <FilterSvg className="tw-text-brand-navy tw-h-5 tw-w-5 tw-mr-1 md:tw-mr-2" />
                      ) : item.kind === 'itineraries' ? (
                        <RouteSvg className="tw-text-brand-navy tw-h-4 tw-w-4 tw-mr-1 md:tw-mr-2" />
                      ) : item.kind === 'collections' ? (
                        <FileSvg className="tw-text-brand-navy tw-h-4 tw-w-4 tw-mr-1 md:tw-mr-2" />
                      ) : item.kind === 'blogs' ? (
                        <MapSvg className="tw-text-brand-navy tw-h-4 tw-w-4 tw-mr-1 md:tw-mr-2" />
                      ) : null}
                      <div>{item.tabName}</div>
                    </div>
                  )}
                </div>
              ))}
          </div>
          <div
            id="next-tab"
            className="tw-h-7 tw-w-7 tw-hidden tw-bg-white tw-rounded-full tw-border-solid tw-border-grey-lighter tw-border tw-p-1.5 tw-ml-4"
          >
            <FiChevronRight
              size={16}
              strokeWidth={2}
              className="tw-cursor-pointer tw-text-brand-navy tw-align-top"
              onClick={() => {
                moveTab(true);
              }}
            />
          </div>
        </div>
      </div>

      <ModalOrDiv
        isModal={!isScreenMd}
        show={popupType ? true : false}
        popupRef={popupRef}
        onClose={() => {
          setPopupType(undefined);
          if (typeof onClose === 'function') {
            onClose();
          }
        }}
      >
        {currentTab ? (
          <>
            {currentTab.header ? (
              <div ref={headerRef} className="tw-pt-5 tw-sticky tw-top-0 tw-bg-brand-yellow tw-z-1200"
              >
                <div className="tw-flex tw-justify-between">
                  <div className="tw-font-semibold tw-font-styleSans md:tw-text-2xl tw-text-xl tw-leading-none md:tw-leading-none">
                    {currentTab.header}
                  </div>
                  <div
                    className="tw-cursor-pointer tw-text-right"
                    onClick={() => {
                      setPopupType(undefined);
                      if (typeof onClose === 'function') {
                        onClose();
                      }
                    }}
                  >
                    <FiX className="tw-text-brand-navy tw-h-5 tw-w-5 md:tw-w-6 md:tw-h-6 " />
                  </div>
                </div>
                <div className="tw-border-t tw-mt-4 md:tw--mx-10 tw--mx-5 tw-border-grey-lighter tw-border-solid"></div>
              </div>
            ) : null}
            {currentTab.sections.map((section, index) => (
              <div key={section.sectionId}>
                <div
                  style={{
                    top: headerRef.current ? `${headerRef.current?.offsetHeight}px` : '0px',
                  }}
                  className="tw-sticky tw-bg-brand-yellow tw-z-100 tw-pt-5"
                >
                  <div className="tw-flex tw-justify-between tw-pb-5">
                    <div className="tw-font-semibold tw-font-styleSans tw-text-lg tw-leading-none md:tw-leading-none">
                      {section.title}
                    </div>
                    {!currentTab.header && index === 0 ? (
                      <div
                        className="tw-cursor-pointer tw-text-right"
                        onClick={() => {
                          setPopupType(undefined);
                          if (typeof onClose === 'function') {
                            onClose();
                          }
                        }}
                      >
                        <FiX className="tw-text-brand-navy tw-h-5 tw-w-5 md:tw-w-6 md:tw-h-6" />
                      </div>
                    ) : null}
                  </div>
                  {!currentTab.header && index === 0 && (
                    <div className="md:tw--mx-10 tw--mx-5 tw-border-t tw-mb-5 tw-border-grey-lighter tw-border-solid"></div>
                  )}
                </div>
                {currentTab.kind === 'filters' ? (
                  <Filters />
                ) : (
                  <div>
                    <div className="tw-flex tw-flex-wrap tw--mx-1.5 tw-mb-4">
                      {section.data
                        .slice(0, showMoreRecords[section.sectionId] ? undefined : DEFAULT_NUMBER_ITEMS)
                        .map((item) => {
                          return (
                            <div key={item.id} className="col-md-4 col-6 tw-px-1.5 tw-mb-3">
                              <ItemCard
                                title={(item.name || item.title) as string}
                                imageUrl={item.coverImgUrl || item.image?.webpMdUrl}
                                link={
                                  section.sectionId === 'cities'
                                    ? AppRoutes.cityRoute({
                                        id: item.slug,
                                      }).toPath()
                                    : section.sectionId === 'other_blogs' ||
                                      section.sectionId === 'video_blogs'
                                    ? AppRoutes.shortMagazineRoute({ id: item.slug }).toPath()
                                    : section.sectionId === 'itineraries'
                                    ? AppRoutes.itineraryRoute({ id: item.slug }).toPath()
                                    : AppRoutes.collectionRoute({ id: item.slug }).toPath()
                                }
                                kind={item.kind}
                              />
                            </div>
                          );
                        })}
                    </div>
                    {currentTab.kind === 'blogs' || currentTab.kind === 'collections' ? (
                      !showMoreRecords[section.sectionId] &&
                      section.data.length > DEFAULT_NUMBER_ITEMS ? (
                        <div
                          className="tw-text-center tw-underline tw-font-styleSans tw-font-semibold tw-cursor-pointer tw-mb-10"
                          onClick={() => {
                            setShowMoreRecords((prev) => ({
                              ...prev,
                              [section.sectionId]: true,
                            }));
                          }}
                        >
                          show more
                        </div>
                      ) : null
                    ) : null}
                    <div
                      className={classNames(
                        'tw-border-t tw-mt-4 tw-border-grey-lighter tw-border-solid',
                        {
                          'tw-hidden': currentTab.sections.length === index + 1,
                        }
                      )}
                    ></div>
                  </div>
                )}
              </div>
            ))}
          </>
        ) : null}
      </ModalOrDiv>

      <ClientOnlyPortal selector="#bottom-body">
        {isScreenMd ? (
          <div
            onClick={() => {
              setPopupType(undefined);
              if (typeof onClose === 'function') {
                onClose();
              }
            }}
            className={classNames(
              'tw-bg-black tw-bottom-0 tw-left-0 tw-opacity-30 tw-fixed tw-w-screen tw-z-90',
              {
                'tw-hidden': !popupType,
              }
            )}
            style={{ height: '100vh' }}
          ></div>
        ) : null}
      </ClientOnlyPortal>
    </>
  ) : null;
};

export default CountryCityPageNav;
